// ══════════════════════════════════════════════
//  子组件：InitiateDrawer — 发起转移抽屉（单页联动）
// ══════════════════════════════════════════════
import React, { useState } from 'react';
import {
  Drawer, Form, Select, Radio, Upload, Button, Alert, Input, message, Tag, Divider, Typography, Card, Row, Col
} from 'antd';
import { UploadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { mockProducts, mockEnterprises, mockOrders, mockConflictLicenses } from './mockData';

const { Text } = Typography;

const InitiateDrawer = ({ open, onCancel, onSuccess }) => {
  const [form] = Form.useForm();

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deviceMode, setDeviceMode] = useState('all');
  const [conflictSimulate, setConflictSimulate] = useState(false);
  const [showConflict, setShowConflict] = useState(false);

  const resetAll = () => {
    form.resetFields();
    setSelectedProduct(null);
    setDeviceMode('all');
    setConflictSimulate(false);
    setShowConflict(false);
    onCancel();
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      if (conflictSimulate) {
        setShowConflict(true);
        return;
      }
      message.success('设备转移发起成功！任务已生成，等待对方确认。');
      resetAll();
      onSuccess();
    } catch (error) {
      // validation failed
    }
  };

  const handleExcludeConflict = () => {
    setShowConflict(false);
    setConflictSimulate(false);
    message.success('已排除冲突设备，可继续提交');
  };

  return (
    <Drawer
      title="发起设备转移"
      placement="right"
      width={680}
      open={open}
      onClose={resetAll}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={resetAll}>取消</Button>
          <Button type="primary" onClick={handleSubmit}>确认发起</Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        {/* ── 接收企业 ── */}
        <Form.Item
          name="targetEnterprise"
          label="接收企业"
          rules={[{ required: true, message: '请选择接收企业' }]}
          extra="仅可选择合作企业作为接收方"
        >
          <Select
            showSearch
            placeholder="搜索合作企业名称或 ID"
            optionFilterProp="label"
            allowClear
            options={mockEnterprises.map(ent => ({
              value: ent.id,
              label: `${ent.name} (${ent.id})`,
            }))}
          />
        </Form.Item>

        {/* ── 转移产品 ── */}
        <Form.Item
          name="productId"
          label="转移产品"
          rules={[{ required: true, message: '请选择要转移的产品' }]}
          extra="仅展示您名下可转移的产品（不包括已下架产品）"
        >
          <Select
            showSearch
            placeholder="选择产品"
            optionFilterProp="label"
            allowClear
            onChange={(val) => {
              const prod = mockProducts.find(p => p.id === val);
              setSelectedProduct(prod || null);
              setShowConflict(false);
            }}
            options={mockProducts.map(p => ({
              value: p.id,
              label: `${p.name} (${p.model}) — 可用 ${p.deviceCount} 台`,
            }))}
          />
        </Form.Item>

        {/* ── 选中产品后的信息 ── */}
        {selectedProduct && (
          <Card size="small" bordered={false} style={{ background: '#f0f5ff', borderRadius: 8, marginBottom: 16 }}>
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <Text type="secondary">产品名称</Text>
                <div style={{ fontWeight: 500 }}>{selectedProduct.name}</div>
              </Col>
              <Col span={12}>
                <Text type="secondary">可用设备数</Text>
                <div style={{ fontWeight: 500, color: '#1890ff', fontSize: 18 }}>{selectedProduct.deviceCount} 台</div>
              </Col>
            </Row>
          </Card>
        )}

        <Divider orientation="left" plain>选择转移设备</Divider>

        {/* ── 冲突模拟开关 ── */}
        <Form.Item label="Mock 冲突模拟" style={{ marginBottom: 8 }}>
          <Radio.Group
            value={conflictSimulate}
            onChange={(e) => { setConflictSimulate(e.target.value); setShowConflict(false); }}
          >
            <Radio value={false}>正常通过</Radio>
            <Radio value={true}>模拟存在转移中的设备</Radio>
          </Radio.Group>
        </Form.Item>

        {/* ── 并发冲突提示 ── */}
        {showConflict && (
          <Alert
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 16, borderRadius: 8 }}
            message="检测到以下设备正在转移中，将被自动排除"
            description={
              <div style={{ marginTop: 8 }}>
                {mockConflictLicenses.map((lic, idx) => (
                  <Tag key={idx} color="orange" style={{ marginBottom: 4, fontFamily: 'monospace' }}>{lic}</Tag>
                ))}
                <div style={{ marginTop: 12 }}>
                  <Button type="primary" size="small" onClick={handleExcludeConflict}>
                    排除冲突，继续转移
                  </Button>
                </div>
              </div>
            }
          />
        )}

        {/* ── 设备选择方式 ── */}
        <Form.Item label="设备选择方式" style={{ marginBottom: 12 }}>
          <Radio.Group
            value={deviceMode}
            onChange={(e) => setDeviceMode(e.target.value)}
            disabled={!selectedProduct}
          >
            <Radio.Button value="all">产品下全部可用设备</Radio.Button>
            <Radio.Button value="order">按生产工单</Radio.Button>
            <Radio.Button value="import">导入 License</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {/* 方式A：全选 */}
        {deviceMode === 'all' && selectedProduct && (
          <div style={{ padding: '12px 16px', background: '#f6ffed', borderRadius: 8, marginBottom: 16, border: '1px solid #b7eb8f' }}>
            <Text>将转移 <Text strong>{selectedProduct.name}</Text> 下全部可用设备，
            共 <Text strong style={{ color: '#1890ff' }}>{selectedProduct.deviceCount}</Text> 台</Text>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>若存在转移中的设备将被自动排除</Text>
            </div>
          </div>
        )}

        {/* 方式B：按生产工单 */}
        {deviceMode === 'order' && (
          <Form.Item
            name="orderId"
            label="选择工单"
            rules={[{ required: true, message: '请选择生产工单' }]}
          >
            <Select
              showSearch
              placeholder="选择已完成或已终止且有 License 的生产工单"
              optionFilterProp="label"
              allowClear
              disabled={!selectedProduct}
              options={mockOrders
                .filter(o =>
                  o.productId === selectedProduct?.id &&
                  (o.status === '已完成' || o.status === '已终止') &&
                  o.licenseCount > 0
                )
                .map(o => ({
                  value: o.id,
                  label: `${o.orderName} (${o.id}) — License ${o.licenseCount}个`,
                }))
              }
            />
          </Form.Item>
        )}

        {/* 方式C：导入 License */}
        {deviceMode === 'import' && (
          <Form.Item
            name="licenseFile"
            label="上传 License 文件"
            extra={
              <span>
                支持 Excel (.xlsx, .xls) 或 CSV 格式，单次最多 10,000 条
                <Button type="link" size="small" style={{ padding: '0 0 0 8px' }} onClick={() => message.info('正在下载 License 导入模板...')}>
                  下载模板
                </Button>
              </span>
            }
          >
            <Upload maxCount={1} accept=".xlsx,.xls,.csv" beforeUpload={() => false}>
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>
        )}

        {/* 未选产品时设备选择区不可用 */}
        {!selectedProduct && (
          <div style={{ padding: '16px', background: '#fafafa', borderRadius: 8, marginBottom: 16, textAlign: 'center' }}>
            <Text type="secondary">请先选择转移产品</Text>
          </div>
        )}

        <Divider orientation="left" plain>转移备注</Divider>

        <Form.Item
          name="remark"
          rules={[{ required: true, message: '请填写转移备注，以便双方追溯' }]}
        >
          <Input.TextArea rows={4} placeholder="请简要说明此次设备调拨的原因或用途" />
        </Form.Item>

        {/* ── 底部警告 ── */}
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ borderRadius: 8 }}
          message="转移须知"
          description={
            <div style={{ fontSize: 13 }}>
              <p>1. 提交后设备将被立即锁定（转移中状态），直到对方确认或拒绝。</p>
              <p>2. 对方确认接收后，您将<Text strong style={{ color: '#ff4d4f' }}>无法恢复</Text>对这些设备的管理权限。</p>
              <p>3. 转移完成后，设备<Text strong>永久禁止</Text>二次转移，已配置的分销链路将被清除。</p>
            </div>
          }
        />
      </Form>
    </Drawer>
  );
};

export default InitiateDrawer;
